import * as React from 'react';
import calculateFieldSizing from '../../utils/calculateFieldSizings';
import calculateIconLocationSizings, { IconLocationType } from '../../utils/calculateIconLocation';
import CalendarSurfaceItemClipping from '../CalendarSurfaceItemClipping';
import getInnerHeight from '../../utils/getInnerHeight';
import { CalendarSurfaceItemContentProps } from './CalendarSurfaceItem.types';
import { observer } from 'mobx-react';
import { renderCharmWrapper, renderIconWrapper } from '../CalendarSurfaceItemComponentRenderer';
import { SurfaceConstants } from 'owa-calendar-surface-core';
let styles = require('../CalendarSurfaceItem.scss');

/**
 * State for the CalendarSurfaceItemOver60
 */
export interface CalendarSurfaceItemOver60State {
    lineHeights: number[];
    subjectLines: number;
    locationLines: number;
    orgLines: number;
    iconLocation: IconLocationType;
}

/**
 * The Component for a calendar surface item over 60 minutes in length
 */
@observer
export default class CalendarSurfaceItemOver60 extends React.Component<
    CalendarSurfaceItemContentProps,
    CalendarSurfaceItemOver60State
> {
    wrapperRef: HTMLElement;
    subjectRef: HTMLElement;
    locationRef: HTMLElement;
    orgRef: HTMLElement;

    constructor(props: CalendarSurfaceItemContentProps) {
        super(props);
        this.state = {
            lineHeights: [0, 0, 0],
            subjectLines: 0,
            locationLines: 0,
            orgLines: 0,
            iconLocation: IconLocationType.Subject,
        };
    }

    render() {
        let { item, colorScheme, charmId } = this.props;
        let { lineHeights, subjectLines, locationLines, orgLines, iconLocation } = this.state;
        const location = item.Location && item.Location.DisplayName;

        return (
            <>
                <div
                    ref={wrapperRef => {
                        this.wrapperRef = wrapperRef;
                    }}
                    className={styles.itemContent}>
                    <h2
                        ref={subjectRef => {
                            this.subjectRef = subjectRef;
                        }}
                        className={styles.itemSubject}>
                        <CalendarSurfaceItemClipping
                            lineHeight={lineHeights[0]}
                            lines={subjectLines}>
                            {renderCharmWrapper(item, colorScheme, charmId)}
                            <span>{item.Subject ? item.Subject : location}</span>
                        </CalendarSurfaceItemClipping>
                    </h2>
                    <div
                        className={styles.itemElementContent}
                        ref={locationRef => {
                            this.locationRef = locationRef;
                        }}>
                        <CalendarSurfaceItemClipping
                            lineHeight={lineHeights[1]}
                            lines={locationLines}>
                            {item.Subject ? location : null}
                        </CalendarSurfaceItemClipping>
                        {iconLocation == IconLocationType.Location &&
                            renderIconWrapper(item, false)}
                    </div>
                    {item.IsMeeting && (
                        <div
                            className={styles.itemElementContent}
                            ref={orgRef => {
                                this.orgRef = orgRef;
                            }}>
                            <CalendarSurfaceItemClipping
                                lineHeight={lineHeights[2]}
                                lines={orgLines}>
                                {item.Organizer && item.Organizer.Mailbox.Name}
                            </CalendarSurfaceItemClipping>
                            {iconLocation == IconLocationType.Organizer &&
                                renderIconWrapper(item, false)}
                        </div>
                    )}
                    {iconLocation == IconLocationType.BelowAll && renderIconWrapper(item, false)}
                </div>
            </>
        );
    }

    public componentDidMount() {
        requestAnimationFrame(() => this.resetFieldSizing());
    }

    private resetFieldSizing() {
        let lineHeights = [
            SurfaceConstants.subjectLineHeight,
            SurfaceConstants.detailsLineHeight,
            SurfaceConstants.detailsLineHeight,
        ];
        let totalHeight = getInnerHeight(this.wrapperRef);
        let subjectHeight = getInnerHeight(this.subjectRef);
        let locationHeight = getInnerHeight(this.locationRef);
        let orgHeight = getInnerHeight(this.orgRef);

        let lines = calculateFieldSizing(lineHeights, totalHeight, [
            subjectHeight,
            locationHeight,
            orgHeight,
        ]);

        // making room for the iconwrapper:
        // if the subject is going down to the bottom, it'll overlap- remove bottom line of subject
        if (subjectHeight >= totalHeight - lineHeights[0] && lines[1] == 0) {
            lines[0] = lines[0] - 1;
        }

        let iconLocation = calculateIconLocationSizings(
            lineHeights,
            lines,
            SurfaceConstants.detailsLineHeight,
            totalHeight
        );

        // https://reactjs.org/docs/react-component.html
        // Calling setState() in this method will trigger an extra rendering, but it will happen before the browser updates the screen.
        // This guarantees that even though the render() will be called twice in this case, the user won’t see the intermediate state.
        // Use this pattern with caution because it often causes performance issues.
        // It can, however, be necessary for cases like modals and tooltips when you need to measure a DOM node before rendering something
        // that depends on its size or position.
        this.setState({
            lineHeights: lineHeights,
            iconLocation: iconLocation,
            subjectLines: lines[0],
            locationLines: lines[1],
            orgLines: lines[2],
        });
    }
}
